import React, {
  useMemo,
  useRef,
  useEffect,
  useCallback,
  RefObject,
} from 'react';

import Tag from '@adsk/alloy-react-tag';
import theme, { StylableComponent } from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import { isGroupHeader } from '../../helpers';
import { defaultRenderGroupHeader } from '../../helpers/defaultRenderers';
import { useTranslation } from '../../i18n';
import { GenericItem, GroupHeader, RenderGroupHeaderProps } from '../../types';

type GroupFilterProps<TItem extends GenericItem> =
  StylableComponent<HTMLButtonElement> & {
    menuItems: TItem[];
    selectedGroupKey?: string;
    setSelectedGroupKey?: (key: string) => void;
    renderGroupHeader?: (props: RenderGroupHeaderProps) => React.ReactNode;
    showGroupItemCount?: boolean;
    isAutocomplete?: boolean;
    setHighlightedIndex?: (index: number) => void;
    refToFocusOnClick?: RefObject<HTMLElement>;
    dropdownId?: string;
  };

const TOKEN_HEIGHT = 26;

const GroupFilter = <TItem extends GenericItem>({
  style = {},
  menuItems,
  selectedGroupKey,
  setSelectedGroupKey,
  renderGroupHeader = defaultRenderGroupHeader,
  showGroupItemCount,
  isAutocomplete,
  setHighlightedIndex,
  refToFocusOnClick,
  dropdownId,
}: GroupFilterProps<TItem>) => {
  const { t } = useTranslation();
  const tokenRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const tokenClickHandler = useCallback(
    (key: string, headerIndex: number) => {
      setSelectedGroupKey?.(key);
      setHighlightedIndex?.(headerIndex);
      refToFocusOnClick?.current?.focus?.();
    },
    [setSelectedGroupKey, setHighlightedIndex, refToFocusOnClick],
  );

  const getTargetProps = useCallback(
    () => ({
      style: { minWidth: 0, maxWidth: '21ch' },
    }),
    [],
  );

  const items = useMemo(() => {
    let cumulativeIndex = 0;

    return menuItems.flatMap((item) => {
      const index = cumulativeIndex;
      if (isGroupHeader?.(item)) {
        const groupHeader = item as GroupHeader;
        const token = (
          <div
            key={groupHeader.groupKey}
            ref={(el) => {
              tokenRefs.current[groupHeader.groupKey] = el;
            }}
          >
            <Tag
              selected={groupHeader.groupKey === selectedGroupKey}
              onChange={() => {
                tokenClickHandler(groupHeader.groupKey, index - 1);
              }}
              disabled={(groupHeader.filteredItemCount ?? 0) <= 0}
              style={{
                margin: `${theme.spacing.XXS}px ${theme.spacing.XXS}px`,
                height: TOKEN_HEIGHT,
              }}
            >
              <OverflowTooltip
                singleLine
                autoTipOnEllipsis
                getTargetProps={getTargetProps}
              >
                {groupHeader.label}
              </OverflowTooltip>
            </Tag>
          </div>
        );
        cumulativeIndex += groupHeader.itemCount;

        return [token];
      }

      return []; // skip nulls
    });
  }, [getTargetProps, menuItems, selectedGroupKey, tokenClickHandler]);

  // scroll to the selected token but only on mount
  useEffect(() => {
    if (selectedGroupKey && tokenRefs?.current[selectedGroupKey]) {
      tokenRefs.current[selectedGroupKey]?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
    // purposely only doing this on mount only as doing it anytime the
    // selectedGroupKey changes causes the menu to jump around
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedGroupItem = useMemo(
    () =>
      menuItems.find(
        (item) => item.groupKey && item.groupKey === selectedGroupKey,
      ) as unknown as GroupHeader,
    [menuItems, selectedGroupKey],
  );

  if (items?.length <= 1) {
    return null;
  }

  return (
    <>
      <div
        css={[
          {
            display: 'flex',
            padding: `${theme.spacing.XS}px 0 ${theme.spacing.XS}px ${theme.spacing.S}px`,
          },
          style,
        ]}
        data-testid="group-filter"
        id={dropdownId ? `${dropdownId}-group-filter` : undefined}
      >
        <Tag
          selected={!selectedGroupKey}
          onChange={() => {
            tokenClickHandler('', -1);
          }}
          style={{
            margin: `${theme.spacing.XXS}px ${theme.spacing.XXS}px`,
            height: TOKEN_HEIGHT,
          }}
        >
          {t('ALL')}
        </Tag>
        <span
          css={[
            {
              border: `1px solid ${theme.tokens.colors.border.inactive.value}`,
              margin: `0 ${theme.spacing.XXS}px`,
            },
          ]}
        />
        <div
          css={[
            {
              display: 'flex',
              overflowX: 'auto',
            },
          ]}
        >
          {items}
        </div>
      </div>
      {selectedGroupItem && (
        <div
          css={[
            {
              height: TOKEN_HEIGHT,
            },
            style,
          ]}
        >
          {renderGroupHeader?.({
            item: selectedGroupItem,
            index: 0,
            showGroupItemCount,
            isAutocomplete,
          })}
        </div>
      )}
    </>
  );
};

export default GroupFilter;
